// src/config/sbsModules.js

export const SBS_MODULES = {
  tms: {
    id: "tms", label: "SBS Smart TMS", shortLabel: "Smart TMS",
    description: "Carrier operations, dispatch, fleet, safety, compliance, and load finance.",
    defaultEnabled: false, status: "active", entitlementEditable: false,
  },
  operations: {
    id: "operations",
    label: "Operations Suite",
    shortLabel: "Operations",
    description:
      "CRM, sales, inventory, tasks, projects, messages, and daily business activity.",
    defaultEnabled: true,
    status: "active",
  },

  finance: {
    id: "finance",
    label: "Finance Center",
    shortLabel: "Finance",
    description:
      "Invoices, accounting, payroll, Rapid Record, break-even tools, financial tracking, and accounting review workflows.",
    defaultEnabled: false,
    status: "active",
  },

  hr: {
    id: "hr",
    label: "HR Center",
    shortLabel: "HR",
    description:
      "Employee 360, organization structure, workforce compliance, secure HR documents, and people administration.",
    defaultEnabled: false,
    status: "active",
  },

  productivity: {
    id: "productivity",
    label: "Productivity & AI",
    shortLabel: "Productivity",
    description:
      "AI tools, templates, proposals, messaging, planning, and document drafting.",
    defaultEnabled: false,
    status: "active",
  },

  cigarRetail: {
    id: "cigarRetail",
    label: "Cigar Retail Suite",
    shortLabel: "Cigar Retail",
    description:
      "Company-scoped cigar lounge inventory, employee-controlled POS, customer records, register accountability, and finance review.",
    defaultEnabled: false,
    status: "active",
  },

  distribution: {
    id: "distribution",
    label: "Distribution Module",
    shortLabel: "Distribution",
    description:
      "B2B distribution, customer purchase orders, manufacturer orders, product databases, and fulfillment tracking.",
    defaultEnabled: false,
    status: "active",
    entitlementEditable: false,
  },

  admin: {
    id: "admin",
    label: "Company Control Center",
    shortLabel: "Admin",
    description:
      "Branding, team seats, roles, client access, modules, widgets, quick links, and company messages.",
    defaultEnabled: true,
    status: "active",
    adminOnly: true,
    entitlementEditable: false,
  },

  clientPortal: {
    id: "clientPortal",
    label: "Client Portal",
    shortLabel: "Client Portal",
    description:
      "Limited client-facing access for projects, messages, updates, and shared company information.",
    defaultEnabled: false,
    status: "active",
    clientOnly: true,
  },
};

export const SBS_TOOLS = {
  smartTms: {
    id: "smartTms", label: "SBS Smart TMS", module: "tms", type: "module",
    description: "Loads, drivers, equipment, Safety & Compliance, and Finance handoff.",
    route: "/tms", dashboardVisible: true, status: "active",
  },
  operationsDashboard: {
    id: "operationsDashboard",
    label: "Operations Dashboard",
    module: "operations",
    type: "dashboard",
    description:
      "Main command center for daily business activity, widgets, navigation, and operational summaries.",
    route: "/operations",
    dashboardVisible: true,
    status: "active",
  },

  crm: {
    id: "crm",
    label: "CRM",
    module: "operations",
    type: "module",
    description:
      "Manage customer relationships, contacts, clients, deals, projects, tasks, and activity.",
    route: "/crm",
    dashboardVisible: true,
    status: "active",
  },

  crmContacts: {
    id: "crmContacts",
    label: "Contacts",
    module: "operations",
    type: "tool",
    description:
      "Store and manage individual contacts connected to clients, deals, projects, and follow-ups.",
    route: "/crm/contacts",
    dashboardVisible: false,
    status: "active",
  },

  crmClients: {
    id: "crmClients",
    label: "Clients",
    module: "operations",
    type: "tool",
    description:
      "Manage client records, business profiles, relationships, and client-facing access connections.",
    route: "/crm/clients",
    dashboardVisible: false,
    status: "active",
  },

  crmDeals: {
    id: "crmDeals",
    label: "Deals",
    module: "operations",
    type: "tool",
    description:
      "Track sales opportunities, deal stages, probability, expected value, and close activity.",
    route: "/crm/deals",
    dashboardVisible: false,
    status: "active",
  },

  crmProjects: {
    id: "crmProjects",
    label: "Projects",
    module: "operations",
    type: "tool",
    description:
      "Organize client and internal projects with status, details, ownership, and visibility controls.",
    route: "/crm/projects",
    dashboardVisible: false,
    status: "active",
  },

  crmTasks: {
    id: "crmTasks",
    label: "CRM Tasks",
    module: "operations",
    type: "tool",
    description:
      "Track CRM-related tasks, follow-ups, due dates, and operational responsibilities.",
    route: "/crm/tasks",
    dashboardVisible: false,
    status: "active",
  },

  salesTracker: {
    id: "salesTracker",
    label: "Sales Tracker",
    module: "operations",
    secondaryModules: ["finance"],
    type: "tool",
    description:
      "Track sales records, quantities, pricing, cost, gross profit, customers, and payment methods.",
    dashboardVisible: true,
    currentLocation: "businessToolkit",
    feedsAccounting: true,
    accountingBridgeSource: true,
    status: "active",
  },

  inventoryManager: {
    id: "inventoryManager",
    label: "Inventory Manager",
    module: "operations",
    secondaryModules: ["finance"],
    type: "tool",
    description:
      "Manage inventory items, stock levels, costs, retail pricing, suppliers, and reorder needs.",
    dashboardVisible: true,
    currentLocation: "businessToolkit",
    status: "active",
  },

  sopBuilder: {
    id: "sopBuilder",
    label: "SOP / Process Builder",
    module: "operations",
    secondaryModules: ["productivity"],
    type: "tool",
    description:
      "Create standard operating procedures and repeatable workflows for business processes.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    status: "active",
  },

  financeCenter: {
    id: "financeCenter",
    label: "Finance Center",
    module: "finance",
    type: "module",
    description:
      "Central finance workspace for accounting, invoices, payroll, Rapid Record, financial summaries, and accounting review.",
    route: "/finance",
    dashboardVisible: true,
    receivesAccountingReview: true,
    status: "active",
  },

  transactions: {
    id: "transactions",
    label: "Rapid Record",
    module: "finance",
    type: "tool",
    description:
      "Quickly capture income, expenses, receipts, invoices, and payments for accounting review.",
    route: "/transactions",
    dashboardVisible: false,
    status: "active",
  },

  payroll: {
    id: "payroll",
    label: "Payroll",
    module: "finance",
    type: "tool",
    description:
      "Manage payroll-related records, pay tracking, and compensation workflows.",
    route: "/payroll",
    dashboardVisible: false,
    status: "active",
  },

  hrCenter: {
    id: "hrCenter",
    label: "SBS HR Center",
    module: "hr",
    type: "module",
    description:
      "Manage Employee 360 records, organization structure, workforce compliance, HR access, and secure employee documents from one people workspace.",
    route: "/hr",
    dashboardVisible: true,
    status: "active",
  },

  invoiceGenerator: {
    id: "invoiceGenerator",
    label: "Invoice Generator",
    module: "operations",
    secondaryModules: ["finance"],
    type: "tool",
    description:
      "Create invoices from operational business activity and send paid invoice data into finance review.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    alsoShownIn: ["financeCenter"],
    feedsAccounting: true,
    accountingBridgeSource: true,
    status: "active",
	
  },

  accountingManager: {
    id: "accountingManager",
    label: "Accounting Manager",
    module: "finance",
    type: "tool",
    description:
      "Manage accounting entries, Accounting Review Queue items, receipts, posting workflows, and finance records.",
    dashboardVisible: true,
    primaryLocation: "financeCenter",
    receivesAccountingReview: true,
    receivesFrom: ["salesTracker", "invoiceGenerator"],
    status: "active",

  },

  breakEvenCalculator: {
    id: "breakEvenCalculator",
    label: "Break-Even Calculator",
    module: "finance",
    type: "tool",
    description:
      "Specialty cigar economics calculator for premium cigar production, batch costing, and vitola profitability.",
    route: "/tools/cigar-break-even",
    dashboardVisible: false,
    
    specialtyModule: "cigarEconomics",
    productionReady: true,
    status: "specialty",
  },

  financialOverview: {
    id: "financialOverview",
    label: "Financial Overview",
    module: "finance",
    type: "widget",
    description:
      "Display live income, expense, and net totals from stored financial records.",
    dashboardVisible: true,
    dataSource: "firestore",
    hardcodedData: false,
    productionReady: true,
    status: "active",
  },

  financialSnapshot: {
    id: "financialSnapshot",
    label: "Financial Snapshot",
    module: "finance",
    type: "widget",
    description:
      "Prototype visual snapshot for income and expense targets. Not production-ready until connected to live financial records.",
    dashboardVisible: false,
    dataSource: "hardcodedSample",
    hardcodedData: true,
    productionReady: false,
    status: "prototype",
  },

  marketingCenter: {
    id: "marketingCenter",
    label: "SBS Marketing Center",
    module: "operations",
    type: "module",
    description:
      "Build marketing strategies, plan campaigns and promotions, create channel-specific content, manage public communications, organize execution, and evaluate results from one business marketing workspace.",
    route: "/marketing-center",
    dashboardVisible: false,
    status: "active",
  },

  aiMessage: {
    id: "aiMessage",
    label: "AI Message Creator",
    module: "productivity",
    type: "tool",
    description:
      "Draft customer messages, announcements, replies, and internal business communications.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    status: "active",
  },

  aiLegal: {
    id: "aiLegal",
    label: "AI Legal Doc Generator",
    module: "productivity",
    type: "tool",
    description:
      "Draft general business document templates for review. This tool does not replace legal advice.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    requiresDisclaimer: true,
    status: "active",
  },

  proposalGen: {
    id: "proposalGen",
    label: "Proposal Generator",
    module: "productivity",
    type: "tool",
    description:
      "Create structured business proposals, service offers, and client-facing pitch documents.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    status: "active",
  },

  businessPlan: {
    id: "businessPlan",
    label: "Business Plan Template",
    module: "productivity",
    type: "tool",
    description:
      "Generate and organize business plan sections, planning notes, and startup documentation.",
    dashboardVisible: false,
    currentLocation: "businessToolkit",
    status: "active",
  },

  cigarRetailSuite: {
    id: "cigarRetailSuite",
    label: "Cigar Retail Suite",
    module: "cigarRetail",
    type: "module",
    description:
      "Run cigar lounge inventory, employee PIN-controlled sales, customer records, shift closeout, and finance review from one workspace.",
    route: "/cigar-retail",
    dashboardVisible: true,
    feedsAccounting: true,
    accountingBridgeSource: true,
    status: "active",
  },

  distribution: {
    id: "distribution",
    label: "Distribution",
    module: "distribution",
    type: "module",
    description:
      "Manage B2B distribution workflows, customer purchase orders, manufacturer production orders, and product databases.",
    route: "/distribution",
    dashboardVisible: true,
    status: "active",
  },

  companyAdmin: {
    id: "companyAdmin",
    label: "Company Control Center",
    module: "admin",
    type: "admin",
    description:
      "Manage company profile, branding, users, roles, widgets, quick links, client access, messages, and modules.",
    route: "/admin",
    dashboardVisible: false,
    status: "active",
  },

  clientPortal: {
    id: "clientPortal",
    label: "Client Portal",
    module: "clientPortal",
    type: "portal",
    description:
      "Limited client-facing workspace for projects, updates, and shared company information.",
    route: "/client",
    dashboardVisible: false,
    status: "active",
  },
};

export function getToolsByModule(moduleId) {
  return Object.values(SBS_TOOLS).filter((tool) => tool.module === moduleId);
}

export function getDashboardTools() {
  return Object.values(SBS_TOOLS).filter((tool) => tool.dashboardVisible);
}

export function getToolById(toolId) {
  return SBS_TOOLS[toolId] || null;
}

export function getModuleById(moduleId) {
  return SBS_MODULES[moduleId] || null;
}

export function getModulesForAdmin() {
  return Object.values(SBS_MODULES).filter((module) => !module.clientOnly);
}

export function getCustomerFacingModules() {
  return Object.values(SBS_MODULES).filter(
    (module) => !module.adminOnly && !module.clientOnly
  );
}

export function getToolsByCurrentLocation(location) {
  return Object.values(SBS_TOOLS).filter(
    (tool) => tool.currentLocation === location
  );
}

export function getToolsWithRoutes() {
  return Object.values(SBS_TOOLS).filter((tool) => Boolean(tool.route));
}

export function getProductionReadyTools() {
  return Object.values(SBS_TOOLS).filter(
    (tool) => tool.productionReady !== false && 
	  tool.status !== "prototype" &&
	  tool.status !== "specialty"
  );
}

export function getPrototypeTools() {
  return Object.values(SBS_TOOLS).filter((tool) => tool.status === "prototype");
}

export function getAccountingBridgeSources() {
  return Object.values(SBS_TOOLS).filter((tool) => tool.accountingBridgeSource);
}

export function getToolsReceivingAccountingReview() {
  return Object.values(SBS_TOOLS).filter((tool) => tool.receivesAccountingReview);
}
